import { Camera, Loader2, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'
import type {
	CameraPermission,
	CameraPermissionState,
} from '@/hooks/use-camera-permission'
import { cn } from '@/lib/utils'
import { getBlockedMessage, getCameraErrorMessage } from '@/utils/camera-error'

const DOT_CLASS: Record<CameraPermissionState, string> = {
	granted: 'bg-green-500',
	denied: 'bg-red-500',
	unsupported: 'bg-red-500',
	prompt: 'bg-amber-400',
	checking: 'bg-muted-foreground/40 animate-pulse',
}

/** Semáforo da câmera: verde liberada, vermelho sem permissão, amarelo ainda não confirmada. */
export function CameraStatusDot({
	state,
	className,
}: {
	state: CameraPermissionState
	className?: string
}) {
	const { t } = useTranslation()
	return (
		<span
			aria-label={t(`qrScanner.permission.${state}`)}
			className={cn('size-2.5 rounded-full', DOT_CLASS[state], className)}
			role="img"
			title={t(`qrScanner.permission.${state}`)}
		/>
	)
}

export function CameraStatusLabel({ state }: { state: CameraPermissionState }) {
	const { t } = useTranslation()
	return (
		<span className="inline-flex items-center gap-2 font-medium text-sm">
			<CameraStatusDot state={state} />
			{t(`qrScanner.permission.${state}`)}
		</span>
	)
}

/**
 * Só mostra a câmera (children) com a permissão liberada. Sem ela, mostra o status e um botão
 * que pede a câmera a partir do toque, que é o que o iOS exige para exibir o aviso.
 */
export function CameraGate({
	camera,
	children,
}: {
	camera: CameraPermission
	children: React.ReactNode
}) {
	const { t } = useTranslation()
	const [asking, setAsking] = useState(false)
	const { state, request, refresh } = camera

	if (state === 'granted') return <>{children}</>

	const ask = async () => {
		setAsking(true)
		const result = await request()
		setAsking(false)
		if (!result.granted && result.error) {
			toast.error(getCameraErrorMessage(result.error, t), {
				autoClose: 10000,
			})
		}
	}

	return (
		<div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-5 text-center">
			<CameraStatusLabel state={state} />
			<p className="text-muted-foreground text-xs leading-snug">
				{state === 'denied'
					? getBlockedMessage(t)
					: t(`qrScanner.permission.${state}Hint`)}
			</p>
			{state === 'prompt' && (
				<Button className="gap-2" disabled={asking} onClick={ask} type="button">
					{asking ? (
						<Loader2 className="size-4 animate-spin" />
					) : (
						<Camera className="size-4" />
					)}
					{t('qrScanner.permission.allow')}
				</Button>
			)}
			{(state === 'denied' || state === 'checking') && (
				<Button
					className="gap-2"
					onClick={() => {
						refresh()
						ask()
					}}
					type="button"
					variant="outline"
				>
					<RefreshCw className="size-4" />
					{t('qrScanner.permission.checkAgain')}
				</Button>
			)}
		</div>
	)
}
